import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Card, 
  CardMedia, 
  CardContent,
  Button,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardActions,
  Chip
} from '@mui/material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useParams, useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';

function CategoryDetail() {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('featured');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategoryAndProducts = async () => {
      try {
        setLoading(true);
        
        // Önce kategoriyi bul
        const categoriesRef = collection(db, 'categories');
        const q = query(categoriesRef, where('slug', '==', slug));
        const categorySnapshot = await getDocs(q);
        
        if (!categorySnapshot.empty) {
          const categoryData = {
            id: categorySnapshot.docs[0].id,
            ...categorySnapshot.docs[0].data()
          };
          setCategory(categoryData);
          
          // Sonra ürünleri getir
          const productsRef = collection(db, 'products');
          const productsQuery = query(productsRef, where('category', '==', slug));
          const productsSnapshot = await getDocs(productsQuery);
          
          const productsData = productsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setProducts(productsData);
        }
      } catch (error) {
        console.error('Veri yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryAndProducts();
  }, [slug]);

  const sortProducts = (products) => {
    switch (sortBy) {
      case 'price-asc':
        return [...products].sort((a, b) => a.price - b.price);
      case 'price-desc':
        return [...products].sort((a, b) => b.price - a.price);
      case 'featured':
        return [...products].sort((a, b) => b.featured - a.featured);
      default:
        return products;
    }
  };

  const renderProductImage = (images) => {
    if (!images || images.length === 0) {
      return '/placeholder-image.jpg'; // Varsayılan bir resim yolu
    }
    return images[0];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <SEO 
        title={`${category?.name} Koleksiyonu`}
        description={`${category?.name} kategorisinde en trend ürünler. ${products.length} farklı model ile şık ve rahat giyim seçenekleri.`}
      />
      <Box sx={{ 
        minHeight: '100vh',
        mt: { xs: '56px', sm: '64px' }, // Header yüksekliği kadar margin
        pt: 4 // Üstten extra padding
      }}>
        <Container maxWidth="lg">
          <Breadcrumbs sx={{ mb: 4 }}>
            <Link 
              component={RouterLink}
              to="/" 
              color="inherit"
            >
              Ana Sayfa
            </Link>
            <Typography color="text.primary">
              {category?.name || 'Kategori'}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 4 
          }}>
            <Typography variant="h4" component="h1">
              {category?.name || 'Kategori'}
            </Typography>
            
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Sırala</InputLabel>
              <Select
                value={sortBy}
                label="Sırala"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="featured">Öne Çıkanlar</MenuItem>
                <MenuItem value="price-asc">Fiyat: Düşükten Yükseğe</MenuItem>
                <MenuItem value="price-desc">Fiyat: Yüksekten Düşüğe</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={3}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card
                  component={RouterLink}
                  to={`/product/${product.id}`}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      '& .MuiCardMedia-root': {
                        transform: 'scale(1.05)'
                      }
                    }
                  }}
                >
                  <Box sx={{ 
                    position: 'relative',
                    overflow: 'hidden',
                    pt: '120%' // 5:6 aspect ratio
                  }}>
                    <CardMedia
                      component="img"
                      image={renderProductImage(product.images)}
                      alt={product.title}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                    {product.discount && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          left: 16,
                          bgcolor: 'error.main',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 'bold'
                        }}
                      >
                        %{product.discount}
                      </Box>
                    )}
                  </Box>

                  <CardContent sx={{ 
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}>
                    <Typography 
                      variant="h6" 
                      component="h2"
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: 'text.primary',
                        mb: 1
                      }}
                    >
                      {product.title}
                    </Typography>

                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 'auto'
                    }}>
                      <Typography 
                        variant="h6" 
                        color="primary"
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }}
                      >
                        {product.price?.toLocaleString('tr-TR')} TL
                      </Typography>
                      {product.oldPrice && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            textDecoration: 'line-through',
                            fontSize: '0.9rem'
                          }}
                        >
                          {product.oldPrice?.toLocaleString('tr-TR')} TL
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.5, 
                      flexWrap: 'wrap',
                      mt: 1
                    }}>
                      {product.sizes?.map((size) => (
                        <Chip
                          key={size}
                          label={size}
                          size="small"
                          sx={{ 
                            bgcolor: 'grey.100',
                            fontSize: '0.75rem'
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default CategoryDetail; 